import React from "react";
import {
  Box,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import HomeIcon from "@mui/icons-material/Home";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import DownloadForOfflineRoundedIcon from "@mui/icons-material/DownloadForOfflineRounded";
import BarChartIcon from "@mui/icons-material/BarChart";
import { useLocation, useNavigate } from "react-router-dom";

const menuItems = [
  {
    id: "home",
    label: "Home",
    icon: <HomeIcon />,
    path: "/",
  },
  {
    id: "upload",
    label: "Upload Data",
    icon: <UploadFileRoundedIcon />,
    path: "/upload",
  },
  {
    id: "download",
    label: "Download Report",
    icon: <DownloadForOfflineRoundedIcon />,
    path: "/download",
  },
  {
    id: "trend",
    label: "Trend",
    icon: <BarChartIcon />,
    path: "/trends",
  },
];

const MenuBar = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <Box
      component="aside"
      sx={{
        flexShrink: 0,
        height: "calc(120vh - 90px)",
        pt: 8,
      }}
    >
      <Paper
        elevation={2}
        sx={{
          p: 3,
          borderRadius: "65px",
          backgroundColor: theme.palette.background.paper,

          borderTop: `4px solid ${theme.palette.secondary.main}`,
          borderBottom: `4px solid ${theme.palette.secondary.main}`,

          width: "100%",
          display: "flex",
          flexDirection: "column",
          maxHeight: "100%",
          overflow: "hidden",
          boxShadow: "0 10px 25px rgba(0,0,0,0.08)",
        }}>

        {/* ===== MENU TITLE ===== */}
        <Typography
          sx={{
            mb: 2,
            fontFamily: "'Poppins', sans-serif",
            fontWeight: 800,
            fontSize: "1.5rem",
            letterSpacing: "1.2px",
            textTransform: "uppercase",
            background: "linear-gradient(45deg,green)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            textShadow: "2px 2px 6px rgba(0,0,0,0.12)",
            textAlign: "center",
          }}>
          MENU
        </Typography>
        <Divider sx={{ mb: 2 }} />

        {/* ===== MENU LIST ===== */}
        <List
          sx={{
            flex: 1,
            overflow: "visible",
            display: "flex",
            flexDirection: "column",
            gap: 0.8,
            px: 0.5,
          }}>
          {menuItems.map((item) => {
            const isActive = location.pathname === item.path;

            return (
              <ListItemButton
                key={item.id}
                selected={isActive}
                onClick={() => navigate(item.path)}
                sx={{
                  borderRadius: "12px",
                  px: 2.5,
                  py: 1.6,
                  transition: "all 0.2s ease",

                  backgroundColor: isActive
                    ? "rgba(255,144,22,0.12)"
                    : "transparent",

                  "&:hover": {
                    backgroundColor: isActive
                      ? "rgba(255,144,22,0.16)"
                      : "rgba(18,90,54,0.05)",

                    transform: "translateX(3px)",

                    "& .MuiListItemIcon-root": {
                      color: theme.palette.secondary.main,
                    },
                  },

                  "&.Mui-selected": {
                    boxShadow: "0 4px 12px rgba(255,144,22,0.12)",
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 75,
                    paddingTop: 0.4,

                    color: isActive
                      ? theme.palette.secondary.main
                      : "#2e2e2e",

                    transition: "color 0.2s",
                    "& svg": {
                      fontSize: "24px",
                    },
                  }}
                >
                  {item.icon}
                </ListItemIcon>

                {/* ===== TEXT SECTION ===== */}
                <ListItemText
                  primary={
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontStyle: isActive ? "inherit" : "normal",
                        textTransform: "uppercase",
                        fontSize: "16px",
                        letterSpacing: "0.2px",
                        fontWeight: isActive ? 750 : 700,

                        color: isActive
                          ? theme.palette.primary.main
                          : theme.palette.text.primary,

                        transition: "all 0.2s",
                      }}
                    >
                      {item.label}
                    </Typography>
                  }
                />

              </ListItemButton>
            );
          })}
        </List>
      </Paper>
    </Box>
  );
};

export default MenuBar;